// Enabler E7-B (ADO-2273), AC15 restated by D-15, AC19, OP-6: whether a stock quantity shows.
//
// SL1.0 shows the quantity in every environment except the core depots (Ambient, Bond, Chill,
// ChillFruitVeg, Frozen, CorribRetail), and to a Bond rep (`engine/stock_and_subs.php:51`).
// On this platform that is:
//   - the `WOW` and `LSN` entries, and a value centre's `DEPOT` entry (`isValueCentre`, the
//     depot directory's answer); Elite is retired, so there is no `ELITE` case (D-15);
//   - a `SalesRep` on the Bond entry, `DEPOT:85` (OP-6: SL1.0's `RepCode` and not
//     `SupplierUserID`, at `Bond`).
// Every other entry (a core `DEPOT` entry, `FRUIT_AND_VEG`, `CORRIB_RETAIL`), and no active
// entry at all, hides it. Where it is hidden, a surface that needs a label shows SL1.0's
// "In stock" (`stock_and_subs.php:81-90`) and the others drop the count.
//
// DISPLAY ONLY (NFR18): the quantity is on the wire either way (`quantityOnHand`); this rule
// decides what the screen prints, never what the caller may read.

import type { OrderingEntry } from '../api/ordering-choices.client';
import type { SessionContext } from '../auth/useSessionStore';
import type { AvailabilityEntry } from './useAvailabilityStore';

/** The Bond depot's code (Bond entry `DEPOT:85`). */
export const BOND_DEPOT_CODE = '85';

/** The label in place of the quantity where it is hidden (SL1.0 `stock_and_subs.php:87-88`). */
export const IN_STOCK_LABEL = 'In stock';

export interface StockQuantityViewer {
  /** The active ordering entry, or `null` when none is chosen. */
  readonly entry: Pick<OrderingEntry, 'kind' | 'depotCode' | 'isValueCentre'> | null;
  /** The session's role code (`SalesRep`, `StoreUser`, ...), or `null` before the read. */
  readonly role: string | null;
}

/** True when the viewer's active entry shows the stock quantity (AC15 restated by D-15). */
export function showsStockQuantity({ entry, role }: StockQuantityViewer): boolean {
  if (entry === null) return false;
  if (entry.kind === 'WOW' || entry.kind === 'LSN') return true;
  if (entry.isValueCentre) return true;
  return role === 'SalesRep' && entry.kind === 'DEPOT' && entry.depotCode === BOND_DEPOT_CODE;
}

/** The rule over the session store: the active entry is the offered entry keyed by
 *  `activeEntryKey`. Returns a boolean, so a component subscribing with it re-renders only when
 *  the answer changes. */
export function selectShowsStockQuantity(
  session: Pick<SessionContext, 'orderingChoices' | 'activeEntryKey' | 'role'>,
): boolean {
  const entry =
    session.orderingChoices?.entries.find(
      (candidate) => candidate.key === session.activeEntryKey,
    ) ?? null;
  return showsStockQuantity({ entry, role: session.role });
}

/** Whether a hidden quantity is replaced by {@link IN_STOCK_LABEL}: SL1.0 says "In stock" for
 *  positive stock the supplier has not blocked (`stock_and_subs.php:65-90`), which is the
 *  `IN_STOCK` and `LOW_STOCK` bands on an `AVAILABLE` product. */
export function isInStockForLabel(availability: AvailabilityEntry): boolean {
  return availability.band !== 'OUT_OF_STOCK' && availability.status !== 'UNAVAILABLE';
}
